import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Check, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import Layout from '../components/layout/Layout';
import { useCart } from '../context/CartContext';
import { ordersAPI } from '../services/api';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const { cartItems, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    comment: '',
    paymentMethod: 'cash'
  });

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  };

  const total = getCartTotal();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.phone) {
      toast.error('Пожалуйста, заполните обязательные поля (ФИО и телефон)');
      return;
    }

    setLoading(true);
    
    try {
      // Prepare order data
      const orderData = {
        items: cartItems.map(item => ({
          id: item.id.toString(),
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          size: item.size,
          selectedPolycarbonate: item.options?.polycarbonate || item.polycarbonateThickness || item.polycarbonate || '4мм',
          selectedOptions: item.selectedOptions || [],
          options: item.options || null,
          calculatorConfig: item.calculatorConfig || null,
          isCustom: item.isCustom || false
        })),
        customer: {
          fullName: formData.fullName,
          phone: formData.phone,
          email: formData.email || 'не указан',
          city: formData.city || 'не указан',
          address: formData.address || 'не указан',
          comment: formData.comment || null,
          paymentMethod: formData.paymentMethod
        },
        total: total
      };

      // Send order to backend
      const result = await ordersAPI.create(orderData);
      
      setOrderNumber(result.id.substring(0, 8).toUpperCase());
      setOrderComplete(true);
      clearCart();
      toast.success('Заказ успешно оформлен!');
    } catch (error) {
      console.error('Order error:', error);
      toast.error('Ошибка при оформлении заказа. Попробуйте снова.');
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0 && !orderComplete) {
    navigate('/cart');
    return null;
  }

  if (orderComplete) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Заказ оформлен!</h1>
            <p className="text-gray-600 mb-2">Спасибо за заказ. Номер вашего заказа:</p>
            <p className="text-2xl font-bold text-emerald-600 mb-6">#{orderNumber}</p>
            <p className="text-gray-600 mb-8">
              Мы свяжемся с вами в ближайшее время для подтверждения.
            </p>
            <Link to="/catalog">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                Продолжить покупки
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumbs */}
      <div className="bg-gray-50 py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-emerald-600">Главная</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/cart" className="hover:text-emerald-600">Корзина</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">Оформление</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Оформление заказа</h1>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Form */}
          <div className="flex-1">
            <form onSubmit={handleSubmit}>
              {/* Contact Info */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                <h2 className="text-xl font-semibold mb-6">Контактные данные</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fullName">ФИО *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Ваше полное имя"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Телефон *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+7 (___) ___-__-__"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="email@example.com"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Delivery */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                <h2 className="text-xl font-semibold mb-6">Адрес доставки</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">Город</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="Например: Уфа"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">Адрес доставки</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Улица, дом, квартира"
                      className="mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="comment">Комментарий к заказу</Label>
                    <Textarea
                      id="comment"
                      name="comment"
                      value={formData.comment}
                      onChange={handleInputChange}
                      placeholder="Дополнительные пожелания..."
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Payment */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                <h2 className="text-xl font-semibold mb-6">Способ оплаты</h2>
                <RadioGroup
                  value={formData.paymentMethod}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, paymentMethod: value }))}
                  className="space-y-3"
                >
                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                    formData.paymentMethod === 'cash' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <RadioGroupItem value="cash" id="cash" />
                    <div className="flex-1">
                      <div className="font-medium">Наличными при получении</div>
                    </div>
                  </label>
                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                    formData.paymentMethod === 'card' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <RadioGroupItem value="card" id="card" />
                    <CreditCard className="w-6 h-6 text-emerald-600" />
                    <div className="flex-1">
                      <div className="font-medium">Банковской картой</div>
                    </div>
                  </label>
                </RadioGroup>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-14 text-base"
                disabled={loading}
              >
                {loading ? 'Оформление...' : `Оформить заказ на ${formatPrice(total)} ₽`}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:w-96">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-4">
              <h2 className="text-xl font-semibold mb-6">Ваш заказ</h2>
              
              <div className="space-y-4 mb-6">
                {cartItems.map(item => (
                  <div key={item.id} className="flex gap-4">
                    {item.images && item.images[0] && (
                      <img 
                        src={item.images[0]} 
                        alt={item.name} 
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="font-medium text-sm mb-1">{item.name}</h3>
                      <p className="text-xs text-gray-500">
                        {item.size.width}x{item.size.length} м × {item.quantity}
                      </p>
                      <p className="text-sm font-semibold text-emerald-600">
                        {formatPrice(item.price * item.quantity)} ₽
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-3">
                <div className="flex justify-between text-lg font-bold">
                  <span>Итого:</span>
                  <span className="text-emerald-600">{formatPrice(total)} ₽</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CheckoutPage;
